import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { campaigns } from "@/data/siteData";

const flowNodes = [
  { label: "Awareness", sub: "Feed builds attention." },
  { label: "Engagement", sub: "Stories drive conversations." },
  { label: "Conversion", sub: "Creators amplify trust." },
];

export default function CampaignShowcase() {
  return (
    <section
      id="campaigns"
      data-testid="campaign-showcase-section"
      className="relative pt-16 md:pt-20 pb-8 md:pb-10 px-6 md:px-10 surface-1"
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div className="max-w-3xl">
            <span className="text-[11px] tracking-[0.28em] uppercase text-[#2563EB] font-bold">
              Campaign Structure
            </span>
            <h2
              data-testid="campaign-headline"
              className="mt-4 font-heading font-bold text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.05] text-slate-900 dark:text-white"
            >
              Great Campaigns Don't Happen{" "}
              <span className="gradient-text">in One Post.</span>
            </h2>
          </div>
          <a
            href="#contact"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 px-5 py-2.5 text-sm text-slate-800 dark:text-white hover:border-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          >
            See all case studies <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        {/* Flow */}
        <div
          data-testid="campaign-flow"
          className="mt-12 rounded-3xl bg-[#F8FAFC] dark:bg-white/[0.03] border border-slate-200 dark:border-white/10 p-6 md:p-8"
        >
          <div className="grid md:grid-cols-3 gap-4 md:gap-6 items-stretch">
            {flowNodes.map((n, i) => (
              <div key={n.label} className="flex items-center gap-3">
                <div className="flex-1 rounded-2xl px-5 py-5 bg-white dark:bg-[#0f2250] ring-1 ring-blue-100 dark:ring-white/10 shadow-[0_10px_30px_-22px_rgba(37,99,235,0.45)]">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#2563EB] font-bold">
                    Stage {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-1 font-heading font-bold text-xl md:text-2xl text-slate-900 dark:text-white">
                    {n.label}
                  </div>
                  <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{n.sub}</div>
                </div>
                {i < flowNodes.length - 1 && (
                  <span className="hidden md:block text-[#2563EB] text-2xl select-none">
                    →
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Featured + stack — equal-height grid, perfectly aligned */}
        <div className="mt-12 grid lg:grid-cols-3 lg:grid-rows-2 gap-4 md:gap-5">
          {/* Large featured — spans 2 rows */}
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            data-testid="campaign-card-0"
            className="group relative overflow-hidden rounded-3xl lg:col-span-2 lg:row-span-2 aspect-[16/11] lg:aspect-auto lg:min-h-[560px]"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_regional-creator-hub/artifacts/xs0la4l7_Gemini_Generated_Image_eo8ka6eo8ka6eo8k%20%281%29.png"
              alt="KFC: 9 for 299 Campaign — behind the scenes"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.03]"
            />
            {/* Bottom-up blue gradient — image visible at top, dark at bottom for legibility */}
            <div
              aria-hidden
              className="absolute inset-0 transition-opacity duration-500"
              style={{
                background:
                  "linear-gradient(to top, rgba(11,28,61,0.96) 0%, rgba(30,58,138,0.78) 28%, rgba(37,99,235,0.32) 55%, rgba(37,99,235,0.05) 80%, transparent 100%)",
              }}
            />
            <div className="absolute top-5 left-5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 px-3 py-1 text-[11px] tracking-wider uppercase text-white">
              {campaigns[0].category}
            </div>
            <div className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center text-white opacity-90 group-hover:bg-[#2563EB] transition-all duration-300">
              <ArrowUpRight className="w-4 h-4" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-7 md:p-9 text-white">
              <h3 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight max-w-xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]">
                KFC: 9 for 299 Campaign
              </h3>
              <div className="mt-6 flex flex-wrap items-end gap-x-10 gap-y-4 max-w-xl">
                {[
                  { v: "5+", label: "Creators" },
                  { v: "10M+", label: "Reach" },
                  { v: "12%", label: "Engagement" },
                ].map((s) => (
                  <div key={s.label}>
                    <div className="font-heading font-extrabold text-3xl md:text-4xl lg:text-[42px] leading-none tracking-tight transition-colors duration-300 group-hover:text-blue-200">
                      {s.v}
                    </div>
                    <div className="mt-1.5 text-[10.5px] tracking-[0.24em] uppercase text-blue-100/85 font-bold">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.article>

          {/* Right stacked smalls — auto-fill 50% of grid height each */}
          {campaigns.slice(1, 3).map((c, i) => (
            <motion.article
              key={c.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: 0.08 + i * 0.08 }}
              data-testid={`campaign-card-${i + 1}`}
              className="group relative overflow-hidden rounded-3xl aspect-[4/3] lg:aspect-auto lg:min-h-0"
            >
              <img
                src={c.image}
                alt={c.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1000ms] group-hover:scale-[1.03]"
              />
              {/* Bottom-up blue gradient overlay */}
              <div
                aria-hidden
                className="absolute inset-0 transition-opacity duration-500"
                style={{
                  background:
                    "linear-gradient(to top, rgba(11,28,61,0.96) 0%, rgba(30,58,138,0.78) 28%, rgba(37,99,235,0.32) 55%, rgba(37,99,235,0.05) 80%, transparent 100%)",
                }}
              />
              <div className="absolute top-4 left-4 rounded-full bg-white/15 backdrop-blur-md border border-white/25 px-2.5 py-0.5 text-[10px] tracking-wider uppercase text-white">
                {c.category}
              </div>
              <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center text-white opacity-90 group-hover:bg-[#2563EB] transition-all duration-300">
                <ArrowUpRight className="w-3.5 h-3.5" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <h3 className="font-heading font-bold text-base md:text-lg leading-tight tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
                  {c.title}
                </h3>
                <div className="mt-3 flex items-end gap-5 flex-wrap">
                  {(c.creators
                    ? [
                        { v: c.creators, label: "Creators" },
                        { v: c.reach, label: "Reach" },
                        { v: c.engagement, label: "Engagement" },
                      ]
                    : [
                        { v: c.views, label: "Views" },
                        { v: c.reach, label: "Reach" },
                      ]
                  ).map((s) => (
                    <div key={s.label}>
                      <div className="font-heading font-extrabold text-lg md:text-xl leading-none tracking-tight transition-colors duration-300 group-hover:text-blue-200">
                        {s.v}
                      </div>
                      <div className="mt-1 text-[9px] tracking-[0.2em] uppercase text-blue-100/85 font-bold">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Wide bottom strip */}
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.55, delay: 0.2 }}
          data-testid="campaign-card-3"
          className="group mt-4 md:mt-5 relative overflow-hidden rounded-3xl aspect-[21/9] min-h-[240px]"
        >
          <img
            src={campaigns[3].image}
            alt={campaigns[3].title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1000ms] group-hover:scale-[1.03]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
            }}
          />
          {/* Bottom-up blue gradient — image visible at top, dark at bottom for text legibility */}
          <div
            aria-hidden
            className="absolute inset-0 transition-opacity duration-500"
            style={{
              background:
                "linear-gradient(to top, rgba(11,28,61,0.96) 0%, rgba(30,58,138,0.78) 28%, rgba(37,99,235,0.32) 55%, rgba(37,99,235,0.05) 80%, transparent 100%)",
            }}
          />
          <div className="absolute top-5 left-5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 px-3 py-1 text-[11px] tracking-wider uppercase text-white">
            {campaigns[3].category}
          </div>
          <div className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center text-white opacity-90 group-hover:bg-[#2563EB] transition-all duration-300">
            <ArrowUpRight className="w-4 h-4" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-7 md:p-9 text-white">
            <h3 className="font-heading font-bold text-2xl md:text-3xl lg:text-[34px] leading-[1.1] tracking-tight max-w-2xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]">
              {campaigns[3].title}
            </h3>
            <div className="mt-5 flex flex-wrap items-end gap-x-10 gap-y-4 max-w-2xl">
              {[
                { v: campaigns[3].creators, label: "Creators" },
                { v: campaigns[3].reach, label: "Reach" },
                { v: campaigns[3].engagement, label: "Engagement" },
              ].map((s) => (
                <div key={s.label}>
                  <div className="font-heading font-extrabold text-2xl md:text-3xl lg:text-[36px] leading-none tracking-tight transition-colors duration-300 group-hover:text-blue-200">
                    {s.v}
                  </div>
                  <div className="mt-1.5 text-[10.5px] tracking-[0.24em] uppercase text-blue-100/85 font-bold">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.article>
      </div>
    </section>
  );
}
